import { Livestream } from '@withjoy/joykit/icons';
import React, { ReactNode } from 'react';
import { ScheduleSection } from '../ScheduleSection';
import { SegmentedControl } from '../SegmentedControl/SegmentedControl';
import { FieldMetadata } from '@shared/components/ScheduleEditor/hooks/useScheduleFormState';
import { ScheduleEditorData } from '@shared/components/ScheduleEditor/types';
import { ButtonV2, DateSelection, Flex, LinkV2, styled, TextV2 } from '@withjoy/joykit';
import { useTranslation } from '@shared/core/i18n';
import { FormTextField } from '../FormTextField';
import { ZoomIcon } from '../icons/Zoom';
import { YoutubeIcon } from '../icons/Youtube';
import { MeetsIcon } from '../icons/Meets';

const VIRTUAL_EVENT_HELP_LINK = 'https://withjoy.com/help/en/articles/8346551-which-streaming-method-is-right-for-me';

const LinkWrapper = styled.div`
  width: fit-content;
  height: fit-content;
  padding: 8px;

  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  border-radius: 8px;
  border: 1px solid var(--mono-3-lines, #ebebeb);
  background: var(--System-White, #fff);

  cursor: pointer;

  * {
    flex-grow: 1;
  }
`;

const LinkContainer = (props: { children: ReactNode; href?: string }) => {
  return (
    <LinkWrapper
      onClick={() => {
        if (props.href && window?.location) {
          window.open(props.href, '_blank', 'noopener,noreferrer');
        }
      }}
    >
      {props.children}
    </LinkWrapper>
  );
};

// Mapping of which components are visible for each selected index
const COMPONENT_VISIBILITY = {
  ['virtualConfig']: [1, 2],
};

const isVisible = (component: keyof typeof COMPONENT_VISIBILITY, selectedIndex: number) => {
  return COMPONENT_VISIBILITY[component].includes(selectedIndex);
};

type TimeDateSectionProps = {
  state: ScheduleEditorData;
  setValue: (key: keyof ScheduleEditorData, value: string | DateSelection) => void;
  meta: Record<keyof ScheduleEditorData, FieldMetadata>;
};

export const EventTypeSection = (props: TimeDateSectionProps) => {
  const { state, setValue } = props;
  const [selectedIndex, setSelectedIndex] = React.useState(state.virtualEventUrl ? 1 : 0);

  const { t } = useTranslation('scheduleEditor');
  const trans = t('form');

  return (
    <ScheduleSection
      icon={<Livestream size={24} />}
      title={'Event Type'}
    >
      <Flex
        gap="10px"
        flexDirection="column"
      >
        <SegmentedControl
          onSelect={(index, _content) => {
            setSelectedIndex(index);

            if (index === 0) {
              props.setValue('virtualEventUrl', null);
            }
          }}
          selectedIndex={selectedIndex}
          segments={[
            {
              label: trans.eventTypeSelector.inPerson(),
            },
            {
              label: trans.eventTypeSelector.virtual(),
            },
            {
              label: trans.eventTypeSelector.both(),
            },
          ]}
        ></SegmentedControl>
        <Flex
          gap="24px"
          flexDirection="column"
        >
          {isVisible('virtualConfig', selectedIndex) && (
            <Flex
              gap="32px"
              flexDirection="column"
            >
              <Flex
                gap="24px"
                flexDirection="column"
              >
                <Flex
                  gap="8px"
                  flexDirection="column"
                >
                  <TextV2 typographyVariant="hed1">{trans.eventTypeSelector.eventTypeHeading()}</TextV2>
                  <TextV2 typographyVariant="body1">{trans.eventTypeSelector.eventTypeBody()}</TextV2>
                </Flex>
                <Flex
                  gap="16px"
                  flexDirection="column"
                >
                  <FormTextField
                    fieldName={'virtualEventUrl'}
                    state={state}
                    setValue={setValue}
                    label={trans.eventTypeSelector.eventLinkHeading()}
                    placeholder={trans.eventTypeSelector.eventLinkPlaceholder()}
                    maxLength={null}
                    meta={props.meta}
                  />
                  <ButtonV2
                    variant="outline"
                    intent="neutral"
                    shape="rounded"
                    disabled={!state.virtualEventUrl}
                    onClick={() => {
                      if (state.virtualEventUrl) {
                        window?.open(state.virtualEventUrl, '_blank', 'noopener,noreferrer');
                      }
                    }}
                  >
                    {trans.eventTypeSelector.testLivestreamCTA()}
                  </ButtonV2>
                </Flex>
              </Flex>
              <Flex
                gap="24px"
                flexDirection="column"
              >
                <Flex
                  gap="16px"
                  flexDirection="column"
                >
                  <TextV2 typographyVariant="hed1">{trans.eventTypeSelector.createStreamHeading()}</TextV2>
                  <Flex gap="16px">
                    <LinkContainer href="https://zoom.us">
                      <ZoomIcon />
                    </LinkContainer>
                    <LinkContainer href="https://www.youtube.com/upload">
                      <YoutubeIcon />
                    </LinkContainer>
                    <LinkContainer href="https://meet.google.com">
                      <MeetsIcon />
                    </LinkContainer>
                  </Flex>
                </Flex>
                <LinkV2
                  typographyVariant="body1"
                  href={VIRTUAL_EVENT_HELP_LINK}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {trans.eventTypeSelector.whichIsRight()}
                </LinkV2>
              </Flex>
            </Flex>
          )}
        </Flex>
      </Flex>
    </ScheduleSection>
  );
};
